import { datadogLogs } from '@datadog/browser-logs';
import { useIsFetching } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { MAIN_SERVICE_PATHS } from '@/constants/navigation';
import useAppReviewPromptEligibilityQuery from '../../api/user/hooks/useAppReviewPromptEligibilityQuery';
import { AutoShowModalId } from '../../store/useAutoShowModalStore';
import { isStageDev } from '../../utils/common';
import useMeQuery from '../../utils/hook/react-query/user/useMeQuery';
import { useAutoShowModalSlot } from '../../utils/hook/useAutoShowModalSlot';
import useClientSide from '../../utils/hook/useClientSide';
import { usePathnameMethod } from '../../utils/hook/usePathnameMethod';
import { isReactNative, requestInAppReview } from '../../utils/react-native';
import { useInAppReviewReshowPolicy } from './useInAppReviewReshowPolicy';

/**
 * 앱 리뷰 유도(In-App Review) 트리거의 노출 조건 판정과 OS 시트 호출을 관리한다.
 *
 * - 앱 전용: 앱 웹뷰 + 로그인 + 띠배너 6개 탭 + 서버 자격(`eligible`) + FE 재노출 정책 통과 시 동작
 * - 노출 조건(구매 3회+·앱 7일+·최근 7일 구매)은 서버가 단일 `eligible`로 판정, 재노출(180일/2회)은 FE localStorage 관리(useInAppReviewReshowPolicy)
 * - 프리프롬프트 없이 OS 시트를 직접 호출하고 즉시 슬롯 반납. 최하위 우선순위라 페이지 쿼리 idle 후에만 호출해 상위 모달을 앞지르지 않는다
 */
export const useInAppReview = () => {
  const { pathname } = usePathnameMethod();

  const [hasTriggered, setHasTriggered] = useState(false);

  const { me } = useMeQuery();

  const isClientSide = useClientSide();
  const { canReshow, recordTrigger } = useInAppReviewReshowPolicy();

  const isLoggedIn = !!me;
  const isVisiblePath = MAIN_SERVICE_PATHS.includes(pathname);

  // 서버 자격 조회 전제 조건 — 앱 웹뷰·로그인·노출 경로·FE 재노출 정책 통과·미트리거일 때만 BE에 자격을 확인한다
  const canCheckEligibility =
    isClientSide &&
    isReactNative() &&
    isLoggedIn &&
    isVisiblePath &&
    canReshow &&
    !hasTriggered;

  const { eligible } = useAppReviewPromptEligibilityQuery({
    enabled: canCheckEligibility,
  });

  const fetchingCount = useIsFetching();

  const enabled = canCheckEligibility && eligible;

  const { isActive } = useAutoShowModalSlot({
    id: AutoShowModalId.IN_APP_REVIEW,
    enabled,
  });

  // 슬롯이 활성화되고 페이지 쿼리가 모두 정착(idle)하면 OS 시트를 1회 호출하고, 트리거를 기록한 뒤 슬롯을 반납한다
  useEffect(() => {
    // 모든 쿼리가 정착(fetching 0)한 뒤에만 발사한다 — 상위 자동노출 모달(마케팅동의·메인팝업 등)의
    // eligibility 응답이 늦어도 먼저 슬롯을 점유하도록 보장하기 위함(최하위 우선순위 보호).
    // 트레이드오프: 전역 fetch에 결합돼 노출 탭에 지속 폴링(refetchInterval)이 생기면 발사가 지연될 수 있다.
    // 상위 모달 eligibility 쿼리만 타깃하면 정밀하나, 상위 6개 모달 queryKey에 강결합되고 신규 모달
    // 누락 시 선점 회귀 위험이 커, 자가 유지되는 전역 게이트를 택했다.
    if (!isActive || fetchingCount > 0 || hasTriggered) return;

    requestInAppReview();
    recordTrigger();
    setHasTriggered(true);

    // 프로덕션 관측용 — 실제 트리거 발사율을 추적한다 (OS 시트 노출 여부는 OS가 비공개)
    datadogLogs.logger.info('[InAppReview] OS 리뷰 시트 호출 요청', {
      pathname,
      userId: me?.id,
    });

    // QA용 — OS 시트는 TestFlight에서 미표시되므로 스테이지/개발에서만 발사를 가시화한다
    if (isStageDev) {
      window.alert('[QA] 인앱 리뷰 트리거 — requestInAppReview 호출됨');
    }
  }, [isActive, fetchingCount, hasTriggered, recordTrigger, pathname, me?.id]);
};
